import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';

const sections = [
  {
    id: 'acceptance',
    icon: 'ri-file-check-line',
    title: '1. Acceptance of Terms',
    content: [
      'By accessing or using the Faasley Foods website (faasley.com), you agree to be bound by these Terms and Conditions. If you do not agree to these terms, please do not use our website or services.',
      'These Terms apply to all visitors, users, and others who access or use the website. We reserve the right to update or modify these Terms at any time without prior notice. Your continued use of the website after any changes constitutes your acceptance of the new Terms.',
    ],
  },
  {
    id: 'products',
    icon: 'ri-store-2-line',
    title: '2. Products & Services',
    content: [
      'Faasley Foods Pvt. Ltd. offers premium packaged food products including Makhana snacks (Fassnut), dry fruits (Fassfalli), Indian spices (Rajsip), tea blends (Faastea / Booston), pickles (Sachar), and drinking water (Watler).',
      'All our products are FSSAI certified and comply with applicable Indian food safety regulations. Product images on the website are for illustrative purposes only; actual product appearance may vary slightly.',
      'We reserve the right to modify, discontinue, or change product offerings at any time without prior notice. Prices are subject to change without notice.',
    ],
  },
  {
    id: 'orders',
    icon: 'ri-shopping-bag-line',
    title: '3. Orders & Purchases',
    content: [
      'Currently, orders can be placed via WhatsApp, phone, or through our contact form. By placing an order, you confirm that you are at least 18 years of age and that all information provided is accurate and complete.',
      'We reserve the right to refuse or cancel any order at our sole discretion, including orders that appear to be placed by dealers, resellers, or distributors without prior agreement.',
      'Payment terms will be communicated at the time of order confirmation. We accept UPI, bank transfer, and cash on delivery for eligible orders.',
    ],
  },
  {
    id: 'shipping',
    icon: 'ri-truck-line',
    title: '4. Shipping & Delivery',
    content: [
      'We currently deliver within India. Delivery timelines vary by location and are typically 3–7 business days from order confirmation. Faasley Foods is not responsible for delays caused by courier partners, natural events, or circumstances beyond our control.',
      'Shipping charges, if applicable, will be communicated at the time of order. Free shipping may be available on orders above a specified minimum value.',
      'Risk of loss and title for products pass to you upon delivery to the carrier.',
    ],
  },
  {
    id: 'returns',
    icon: 'ri-arrow-go-back-line',
    title: '5. Returns & Refunds',
    content: [
      'We take quality seriously. If you receive a damaged, defective, or incorrect product, please contact us within 48 hours of delivery with photographic evidence at pari211284@gmail.com or via WhatsApp at +91 8800889959.',
      'Refunds or replacements will be processed within 5–7 business days after verification. We do not accept returns for products that have been opened, used, or tampered with, unless the product is defective.',
      'Perishable items and products with broken seals are not eligible for return unless they arrive damaged.',
    ],
  },
  {
    id: 'intellectual',
    icon: 'ri-copyright-line',
    title: '6. Intellectual Property',
    content: [
      'All content on this website — including text, graphics, logos, images, brand names (Fassnut, Rajsip, Faastea, Booston, Sachar, Watler, Fassfalli), and software — is the exclusive property of Faasleyfoods Pvt. Ltd. and is protected by applicable intellectual property laws.',
      'You may not reproduce, distribute, modify, or create derivative works from any content on this website without our prior written consent.',
    ],
  },
  {
    id: 'liability',
    icon: 'ri-shield-line',
    title: '7. Limitation of Liability',
    content: [
      'Faasley Foods Pvt. Ltd. shall not be liable for any indirect, incidental, special, or consequential damages arising from your use of our website or products, even if we have been advised of the possibility of such damages.',
      'Our total liability to you for any claim arising from these Terms shall not exceed the amount paid by you for the specific product or service giving rise to the claim.',
    ],
  },
  {
    id: 'governing',
    icon: 'ri-scales-line',
    title: '8. Governing Law',
    content: [
      'These Terms and Conditions are governed by and construed in accordance with the laws of India. Any disputes arising under these Terms shall be subject to the exclusive jurisdiction of the courts located in India.',
      'If any provision of these Terms is found to be unenforceable, the remaining provisions will continue in full force and effect.',
    ],
  },
  {
    id: 'contact',
    icon: 'ri-mail-line',
    title: '9. Contact Us',
    content: [
      'If you have any questions about these Terms and Conditions, please contact us:',
    ],
    contact: true,
  },
];

export default function TermsPage() {
  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'schema-terms';
    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      '@id': `${SITE_URL}/terms#webpage`,
      url: `${SITE_URL}/terms`,
      name: 'Terms & Conditions | Faasley Foods',
      description: 'Read the Terms and Conditions for using the Faasley Foods website and purchasing our premium Indian packaged food products.',
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
          { '@type': 'ListItem', position: 2, name: 'Terms & Conditions', item: `${SITE_URL}/terms` },
        ],
      },
    });
    document.head.appendChild(script);
    document.title = 'Terms & Conditions | Faasley Foods';
    return () => {
      const el = document.getElementById('schema-terms');
      if (el) el.remove();
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <div className="pt-[200px] md:pt-[220px] pb-16 bg-amber-50/60 border-b border-amber-100">
        <div className="w-full px-4 sm:px-6 lg:px-12 xl:px-20">
          <div className="flex items-center gap-2 text-sm text-stone-400 mb-6">
            <Link to="/" className="hover:text-amber-600 transition-colors">Home</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-stone-600">Terms &amp; Conditions</span>
          </div>
          <div className="max-w-3xl">
            <span className="inline-block bg-amber-100 text-amber-700 text-xs font-bold uppercase tracking-[0.2em] px-4 py-1.5 rounded-full mb-5">
              Legal
            </span>
            <h1 className="font-display text-4xl md:text-5xl font-bold text-stone-900 mb-4 leading-tight">
              Terms &amp; Conditions
            </h1>
            <p className="text-stone-500 text-base leading-relaxed max-w-xl">
              Please read these terms carefully before using our website or purchasing our products. Last updated: April 2026.
            </p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="w-full px-4 sm:px-6 lg:px-12 xl:px-20 py-16 md:py-24">
        <div className="grid lg:grid-cols-[260px_1fr] gap-12 lg:gap-16 items-start">

          {/* Sticky Table of Contents */}
          <aside className="hidden lg:block sticky top-28">
            <p className="text-xs font-bold uppercase tracking-widest text-stone-400 mb-4">On This Page</p>
            <nav className="space-y-1">
              {sections.map((s) => (
                <a
                  key={s.id}
                  href={`#${s.id}`}
                  className="flex items-center gap-2.5 text-sm text-stone-400 hover:text-amber-600 py-1.5 transition-colors group"
                >
                  <span className="w-0 group-hover:w-2 h-px bg-amber-500 transition-all flex-shrink-0"></span>
                  {s.title.replace(/^\d+\.\s/, '')}
                </a>
              ))}
            </nav>
            <div className="mt-8 p-5 bg-amber-50 rounded-2xl border border-amber-100">
              <p className="text-xs font-bold text-stone-700 mb-2">Need Help?</p>
              <p className="text-xs text-stone-400 leading-relaxed mb-3">
                Questions about our terms? We&apos;re happy to clarify.
              </p>
              <Link
                to="/contact"
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-amber-600 hover:text-amber-700 transition-colors"
              >
                Contact Us <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </aside>

          {/* Main Content */}
          <main>
            <div className="space-y-12">
              {sections.map((section) => (
                <section key={section.id} id={section.id} className="scroll-mt-28">
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-amber-50 flex-shrink-0">
                      <i className={`${section.icon} text-amber-500 text-base`}></i>
                    </div>
                    <h2 className="font-display text-xl font-bold text-stone-900">{section.title}</h2>
                  </div>
                  <div className="space-y-4 pl-12">
                    {section.content.map((para, i) => (
                      <p key={i} className="text-stone-500 text-sm leading-relaxed">{para}</p>
                    ))}
                    {section.contact && (
                      <div className="mt-4 bg-stone-50 rounded-2xl p-6 border border-stone-100 space-y-3">
                        {[
                          { icon: 'ri-building-line', label: 'Company', value: 'Faasleyfoods Pvt. Ltd.' },
                          { icon: 'ri-mail-line', label: 'Email', value: 'pari211284@gmail.com' },
                          { icon: 'ri-phone-line', label: 'Phone', value: '+91 8800889959' },
                          { icon: 'ri-map-pin-line', label: 'Country', value: 'India' },
                        ].map((item) => (
                          <div key={item.label} className="flex items-center gap-3">
                            <div className="w-7 h-7 flex items-center justify-center flex-shrink-0">
                              <i className={`${item.icon} text-amber-500 text-sm`}></i>
                            </div>
                            <span className="text-xs text-stone-400 w-16 flex-shrink-0">{item.label}</span>
                            <span className="text-sm text-stone-700 font-medium">{item.value}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="mt-10 h-px bg-stone-100"></div>
                </section>
              ))}
            </div>

            {/* Bottom CTA */}
            <div className="mt-12 bg-amber-50 rounded-3xl p-8 border border-amber-100 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
              <div>
                <p className="font-semibold text-stone-900 mb-1">Also read our Privacy Policy</p>
                <p className="text-sm text-stone-400">Understand how we collect and use your data.</p>
              </div>
              <Link
                to="/privacy"
                className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-white font-semibold px-6 py-3 rounded-full text-sm transition-colors whitespace-nowrap"
              >
                Privacy Policy <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </main>
        </div>
      </div>

      <Footer />
    </div>
  );
}
